import { useState, useRef, useLayoutEffect, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useQuery } from "@tanstack/react-query";
import { ChevronsUpDown, PencilIcon, Search, X } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import InputDebounce from "./InputDebounce";
import Avatar from "./Avatar";
import { searchIssues } from "../api/ProjectApi";
import JiraTaskIcon from "../JiraTaskIcon";

interface JiraIssueSelectProps {
  type: "INLINE" | "FORM" | "GRID" | "CUSTOM";
  value: any;
  onChange: (value: any) => void;
  placeholder?: string;
  isClearable?: boolean;
  popoverPanelWidth?: number;
  targetCustom?: React.ReactNode;
}

const JiraIssueSelect = ({
  type,
  value,
  onChange,
  placeholder,
  isClearable,
  popoverPanelWidth,
  targetCustom,
}: JiraIssueSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const [keywords, setKeywords] = useState("");

  const tenantInfo = useSelector((state: any) => state.tenant.get("tenantInfo"));
  const project = useSelector((state: any) => state.projectReducer.get("project"));

  // Same check as SaleForm: tenantInfo.getIn(["host", "product"]) === "JIRA"
  const isJiraTenant = tenantInfo.getIn(["host", "product"]) === "JIRA";

  const projectKeys = project
    .get("projects")
    .map((item: any) => item.get("value"))
    .join(",");

  // Only flag missing projects when the workspace is a Jira tenant
  // (mirrors the guard: {isJira && <ProjectPicker .../>} in ProjectSetting)
  const noProjectsConfigured = isJiraTenant && !projectKeys;

  const { data, isLoading } = useQuery({
    queryKey: ["issues", projectKeys, keywords],
    queryFn: async () => {
      const sectionId = keywords && keywords != "" ? "cs" : "hs";
      const jql = `project in (${projectKeys})`;
      const sections = await searchIssues(keywords, jql);
      const issuesData = [];
      if (sections && Array.isArray(sections)) {
        sections.forEach((section: any) => {
          if (section && section.id == sectionId && section.issues) {
            issuesData.push(...section.issues);
          }
        });
      }
      return issuesData;
    },
    enabled: isOpen && !!projectKeys,
    staleTime: 60 * 1000, // cache 60s
  });

  const issues = fromJS(
    (data || []).map((issue) => ({
      label: `${issue.summaryText}`,
      value: issue.key,
      description: "",
      id: issue.key,
      type: "ISSUE",
      iconUrl: `${tenantInfo.get("tenantUrl")}${issue.img}`,
      projectId: project.get("id"),
    }))
  );

  const renderSearch = () => {
    return (
      <div className="w-full flex items-center">
        <Search className="w-4 h-4 text-gray-400" />
        <InputDebounce
          autoFocus
          className={`w-full h-6 px-2 placeholder:text-gray-400 text-sm focus:outline-none`}
          placeholder={t("Search...")}
          value={keywords}
          onChange={(value: string) => {
            setKeywords(value);
          }}
          delay={600}
        />
      </div>
    );
  };

  const renderJiraList = (onClose) => {
    // Show error when no Jira projects are set up
    if (noProjectsConfigured) {
      return (
        <div className="flex items-center justify-center p-2 text-red-500 text-sm text-center">
          {t("You don't have any associated Jira projects")}
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="flex items-center justify-center p-1 text-gray-400">{t("Loading...")}</div>
      );
    }
    if (issues.size === 0) {
      return (
        <div className="flex items-center justify-center p-1 text-gray-400">{t("No options")}</div>
      );
    }
    return issues.map((item) => {
      return (
        <div
          key={item.get("id")}
          className="flex items-center p-1 rounded-md hover:bg-gray-100 transition-colors gap-2 cursor-default"
          onClick={() => {
            onChange(item.toJS());
            onClose();
          }}
        >
          <div className="flex-none">
            {<Avatar avatarUrl={item.get("iconUrl")} displayName={item.get("label")} />}
          </div>
          <div className="flex flex-col flex-nowrap overflow-hidden">
            <div title={item.get("label")} className="text-sm font-medium leading-4 truncate">
              {item.get("label")}
            </div>
          </div>
        </div>
      );
    });
  };

  const renderTarget = (isOpen) => {
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {noProjectsConfigured ? (
            <div className="w-full text-left font-medium text-xs leading-4 text-red-400">
              {t("You don't have any associated Jira projects")}
            </div>
          ) : value ? (
            <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden cursor-default">
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={5} />
              <div className="text-xs font-medium leading-4">{value.label}</div>
            </div>
          ) : (
            <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            noProjectsConfigured
              ? "border border-red-300 bg-red-50"
              : isOpen
              ? "ring-4 ring-blue-100 border border-blue-600"
              : "border border-gray-200"
          }`}
        >
          {noProjectsConfigured ? (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <JiraTaskIcon className="w-5 h-5 text-red-400" />
              <div className="text-sm leading-4 text-red-400 truncate">
                {t("You don't have any associated Jira projects")}
              </div>
            </div>
          ) : value ? (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
              <div className="text-sm font-medium leading-4 truncate">{value.label}</div>
            </div>
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <JiraTaskIcon className="w-5 h-5 text-gray-500" />
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            {isClearable && !!value ? (
              <X
                className={`w-3.5 h-3.5 ${
                  isOpen ? "text-gray-500 hover:text-gray-700" : "text-gray-300 hover:text-gray-400"
                }`}
                strokeWidth={4}
                onClick={() => {
                  onChange(undefined);
                }}
              />
            ) : null}
            {!noProjectsConfigured && <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {value ? (
            <div className="shrink-0 flex items-center flex-nowrap gap-2 overflow-hidden cursor-default">
              <Avatar avatarUrl={value.iconUrl} displayName={value.label} sizeIcon={6} />
              <div className="text-sm font-medium leading-4">{value.label}</div>
            </div>
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    } else if (type === "CUSTOM") {
      return <>{targetCustom}</>;
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  } else if (type === "CUSTOM") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <Popover>
      {({ open, close }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useLayoutEffect(() => {
          if (!open) return;
          if (!popoverButtonRef.current || !popoverPanelRef.current) return;
          const buttonWidth = popoverButtonRef.current.offsetWidth;
          const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
          const maxWidth = Math.max(buttonWidth, panelNaturalWidth);
          setPanelWidth(popoverPanelWidth || maxWidth);
        }, [open]);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          setIsOpen(open);
        }, [open]);

        return (
          <>
            <PopoverButton
              ref={popoverButtonRef}
              as="div"
              onClick={(e) => {
                if (open) {
                  e.preventDefault();
                }
              }}
            >
              {renderTarget(open)}
            </PopoverButton>
            <PopoverPanel
              ref={popoverPanelRef}
              anchor="bottom start"
              style={{ width: panelWidth ?? "auto" }}
              className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
            >
              <div>
                {renderSearch()}
                <div className="w-full my-1 border-b border-gray-200"></div>
                <div className="w-full max-h-60 overflow-y-auto">{renderJiraList(close)}</div>
              </div>
            </PopoverPanel>
          </>
        );
      }}
    </Popover>
  );
};

export default JiraIssueSelect;
